import * as B from '@blueprintjs/core';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as I from 'immutable';
import {all, create} from 'mathjs';
import React from 'react';

import {api} from 'app/modules/Remote';
import {
  APIAlertPolicyProperties,
  AlertPolicy,
  ApiAlertEnrollment,
  ApiAlertPolicy,
} from 'app/modules/Remote/Feature';
import {AlertTypeProperties, ApiAlertDateRange} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {recordEvent} from 'app/tools/Analytics';
import {camelCaseKeys, snakeCaseKeys} from 'app/utils/apiUtils';
import * as layerUtils from 'app/utils/layerUtils';

import {usePushNotification} from '../Notification';
import {normalizeAlertPolicy} from './alertPolicyUtils';
import {AlertFormState} from './useAlertForm';

const FAILED_CREATE_ERROR =
  'Error creating alert policy, please make sure all required fields are filled out correctly and that your user role is not read-only';
const FAILED_DELETE_ERROR =
  'Error deleting alert policy, please make sure that your user role is not read-only';

interface AlertPolicyContextValue {
  alertPolicies: AlertPolicy[];
  actions: AlertPolicyActions;
  meta: {isFetching: boolean; error: unknown | null};
}

export const AlertPolicyContext = React.createContext<AlertPolicyContextValue | undefined>(
  undefined
);

interface AlertPolicyActions {
  createAlertPolicy: (
    featureCollection: I.ImmutableOf<ApiFeatureCollection>,
    formState: AlertFormState
  ) => Promise<ApiAlertPolicy>;
  updateAlertPolicy: (
    alertPolicy: AlertPolicy,
    formState: AlertFormState
  ) => Promise<ApiAlertPolicy>;
  enrollFeaturesInAlertPolicy: (
    alertPolicy: AlertPolicy,
    featureIds: number[],
    showFetching?: boolean
  ) => Promise<ApiAlertEnrollment[]>;
  unenrollFeaturesInAlertPolicy: (
    alertPolicy: AlertPolicy,
    featureIds: number[]
  ) => Promise<{alertPolicy: AlertPolicy; featureIds: number[]}>;
  archiveAlertPolicy: (alertPolicyId: number) => Promise<ApiAlertPolicy>;
}

function configureAlertProperties(properties: AlertTypeProperties): APIAlertPolicyProperties {
  const percentOfPropertyObj: {percent_of_property?: number} = {};
  let dateRange: ApiAlertDateRange | null = null;
  if ('percentOfProperty' in properties) {
    const math = create(all, {
      number: 'BigNumber',
    });
    const decimal = math.evaluate(`${properties.percentOfProperty} / 100`);
    percentOfPropertyObj.percent_of_property = math.number(decimal);
  }
  // convert the Datepicker's [Date, Date] format to the API's [{month: number; day: number}, {month: number; day: number}] format
  if ('dateRange' in properties) {
    dateRange =
      properties.dateRange && properties.dateRange[0] && properties.dateRange[1]
        ? (properties.dateRange.map((date) => ({
            // Datepicker's getMonth() returns 0-11, but backend expects 1-12, so we add 1
            month: date!.getMonth() + 1,
            day: date!.getDate(),
          })) as ApiAlertDateRange)
        : null;
  }
  // `id` on a RangeCondition is client-only, for React keys — the API has no notion of it and
  // pydantic would just ignore it, but strip it rather than depend on that.
  const strippedProperties =
    'groups' in properties
      ? {
          ...properties,
          groups: properties.groups.map((group) =>
            group.map(({id: _id, ...condition}) => condition)
          ),
        }
      : properties;

  return {
    ...snakeCaseKeys(strippedProperties),
    ...percentOfPropertyObj,
    date_range: dateRange,
  };
}

export const AlertPolicyContextProvider = ({
  children,
  featureCollection,
}: {
  children: React.ReactNode;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
}) => {
  const featureCollectionId = featureCollection.get('id');
  const pushNotification = usePushNotification();
  const queryClient = useQueryClient();

  // Fetch alert policies
  const {
    data: alertPolicies = [],
    isFetching,
    error,
  } = useQuery({
    queryKey: ['alertPolicies', featureCollectionId],
    queryFn: async (): Promise<AlertPolicy[]> => {
      const apiAlertPolicies: I.ImmutableListOf<ApiAlertPolicy> = (
        await api.featureCollections.getAlertPolicies(featureCollectionId)
      ).get('data');
      return (camelCaseKeys(apiAlertPolicies.toJS()) as AlertPolicy[]).map(normalizeAlertPolicy);
    },
  });

  // Reusable function to display error notifications
  function handleError(message: string, e: unknown) {
    console.error(e);
    pushNotification({
      message,
      options: {intent: B.Intent.DANGER},
    });
  }

  // CREATE
  const createAlertPolicyMutation = useMutation({
    mutationFn: async ({
      featureCollection,
      formState,
    }: {
      featureCollection: I.ImmutableOf<ApiFeatureCollection>;
      formState: AlertFormState;
    }) => {
      const {sourceId, layerId} = layerUtils.parseLayerKey(formState.layer.key);
      const response = await api.featureCollections.createAlertPolicy({
        featureCollectionId: featureCollection.get('id'),
        sourceId,
        layerId,
        alertType: formState.alertType,
        properties: configureAlertProperties(formState.properties),
        name: formState.name,
        description: formState.description,
      });
      return response.get('data').toJS() as AlertPolicy;
    },
    onSuccess: (newAlertPolicy, {featureCollection, formState}) => {
      const camelNewAlertPolicy = normalizeAlertPolicy(camelCaseKeys(newAlertPolicy));
      pushNotification({
        message: `Lookout policy "${camelNewAlertPolicy.name}" created successfully`,
        options: {intent: B.Intent.SUCCESS},
      });
      recordEvent('Created Lookout policy', {
        name: camelNewAlertPolicy.name,
        layer: formState.layer.key,
      });
      // Update the local cache after successful mutation
      // (instead of refetch)
      queryClient.setQueryData<AlertPolicy[]>(
        ['alertPolicies', featureCollection.get('id')],
        (prev = []) => [...prev, {...camelNewAlertPolicy, enrollments: []}]
      );
    },
    onError: (err) => {
      handleError(FAILED_CREATE_ERROR, err);
    },
  });

  // UPDATE
  const updateAlertPolicyMutation = useMutation({
    mutationFn: async ({
      alertPolicy,
      formState,
    }: {
      alertPolicy: AlertPolicy;
      formState: AlertFormState;
    }) => {
      const {sourceId, layerId} = layerUtils.parseLayerKey(formState.layer.key);
      const response = await api.featureCollections.updateAlertPolicy({
        id: alertPolicy.id,
        featureCollectionId: alertPolicy.featureCollectionId,
        sourceId,
        layerId,
        alertType: formState.alertType,
        properties: configureAlertProperties(formState.properties),
        name: formState.name,
        description: formState.description,
      });
      return response.get('data').toJS() as AlertPolicy;
    },
    onSuccess: (updatedPolicy, {alertPolicy: prevAlertPolicy}) => {
      const camelUpdatedPolicy = normalizeAlertPolicy(camelCaseKeys(updatedPolicy));
      pushNotification({
        message: `Alert policy "${camelUpdatedPolicy.name}" updated successfully`,
        options: {intent: B.Intent.SUCCESS},
      });
      // Update cache
      queryClient.setQueryData<AlertPolicy[]>(
        ['alertPolicies', prevAlertPolicy.featureCollectionId],
        (prev = []) => {
          return prev.map((p) =>
            p.id === camelUpdatedPolicy.id
              ? {...camelUpdatedPolicy, enrollments: prevAlertPolicy.enrollments}
              : p
          );
        }
      );
    },
    onError: (err) => {
      handleError(FAILED_CREATE_ERROR, err);
    },
  });

  // ARCHIVE
  const archiveAlertPolicyMutation = useMutation({
    mutationFn: async (alertPolicyId: number) => {
      const response = await api.featureCollections.archiveAlertPolicy(
        featureCollectionId,
        alertPolicyId
      );
      return response.get('data').toJS() as AlertPolicy;
    },
    onMutate: (alertPolicyId) => {
      // Cancel any outgoing refetches
      queryClient.cancelQueries({
        queryKey: ['alertPolicies', featureCollectionId],
      });
      const previousPolicies = queryClient.getQueryData<AlertPolicy[]>([
        'alertPolicies',
        featureCollectionId,
      ]);
      queryClient.setQueryData<AlertPolicy[]>(
        ['alertPolicies', featureCollectionId],
        (previousPolicies = []) =>
          previousPolicies.filter((previousPolicy) => previousPolicy.id !== alertPolicyId)
      );

      // This Fn returns the context that onError will receive
      return {previousPolicies};
    },
    onSuccess: (archivedPolicy) => {
      pushNotification({
        message: `${archivedPolicy.name} successfully deleted`,
        options: {intent: B.Intent.SUCCESS},
      });
    },
    onError: (err, _alertPolicyId, context) => {
      handleError(FAILED_DELETE_ERROR, err);
      // rollback on error
      if (context?.previousPolicies) {
        queryClient.setQueryData<AlertPolicy[]>(
          ['alertPolicies', featureCollectionId],
          context.previousPolicies
        );
      } else {
        // invalidate cache if no previous policies
        queryClient.invalidateQueries({
          queryKey: ['alertPolicies', featureCollectionId],
        });
      }
    },
  });

  // ENROLL
  const enrollFeaturesMutation = useMutation({
    mutationFn: async ({
      alertPolicy,
      featureIds,
    }: {
      alertPolicy: AlertPolicy;
      featureIds: number[];
    }) => {
      const response = await api.featureCollections.enrollFeaturesInAlertPolicy(
        alertPolicy.featureCollectionId,
        alertPolicy.id,
        featureIds
      );
      return response.get('data').toJS() as ApiAlertEnrollment[];
    },
    onSuccess: (updatedEnrollments, {alertPolicy}) => {
      pushNotification({
        message: `Successfully enrolled in ${alertPolicy.name}`,
        options: {intent: B.Intent.SUCCESS},
        shouldClearPrevToast: false,
      });
      // Update cache
      queryClient.setQueryData<AlertPolicy[]>(
        ['alertPolicies', alertPolicy.featureCollectionId],
        (prev = []) =>
          prev.map((policy) =>
            policy.id === alertPolicy.id
              ? {...policy, enrollments: [...policy.enrollments, ...updatedEnrollments]}
              : policy
          )
      );
    },
    onError: (err) => {
      handleError(FAILED_CREATE_ERROR, err);
    },
  });

  // UNENROLL
  const unenrollFeaturesMutation = useMutation({
    mutationFn: async ({
      alertPolicy,
      featureIds,
    }: {
      alertPolicy: AlertPolicy;
      featureIds: number[];
    }) => {
      await api.featureCollections.unenrollFeaturesInAlertPolicy(
        alertPolicy.featureCollectionId,
        alertPolicy.id,
        featureIds
      );
      return {alertPolicy, featureIds};
    },
    onMutate: ({alertPolicy, featureIds}) => {
      // It's important to cancel any outgoing refetches
      // so that we don't overwrite any optimistic updates
      // with stale data
      queryClient.cancelQueries({
        queryKey: ['alertPolicies', alertPolicy.featureCollectionId],
      });

      // Store the previous policies in case of error so we can rollback
      const previousPolicies = queryClient.getQueryData<AlertPolicy[]>([
        'alertPolicies',
        alertPolicy.featureCollectionId,
      ]);

      // Update cache
      queryClient.setQueryData<AlertPolicy[]>(
        ['alertPolicies', alertPolicy.featureCollectionId],
        (previousPolicies = []) => {
          return previousPolicies.map((policy) =>
            policy.id === alertPolicy.id
              ? {
                  ...policy,
                  enrollments: policy.enrollments.filter(
                    (enrollment) => !featureIds.includes(enrollment.featureId)
                  ),
                }
              : policy
          );
        }
      );

      // This Fn returns the context that onError will receive
      return {previousPolicies};
    },
    onSuccess: ({alertPolicy}) => {
      pushNotification({
        message: `Successfully unenrolled from ${alertPolicy.name}`,
        options: {intent: B.Intent.SUCCESS},
        shouldClearPrevToast: false,
      });
    },
    onError: (err, _variables, context) => {
      handleError(FAILED_CREATE_ERROR, err);
      // rollback on error
      if (context?.previousPolicies) {
        queryClient.setQueryData<AlertPolicy[]>(
          ['alertPolicies', featureCollectionId],
          context.previousPolicies
        );
      } else {
        // invalidate cache if no previous policies
        queryClient.invalidateQueries({
          queryKey: ['alertPolicies', featureCollectionId],
        });
      }
    },
  });

  // Map the mutation calls back to our context actions
  const actions: AlertPolicyActions = {
    createAlertPolicy: (fc, fs) =>
      createAlertPolicyMutation.mutateAsync({featureCollection: fc, formState: fs}),
    updateAlertPolicy: (policy, fs) =>
      updateAlertPolicyMutation.mutateAsync({alertPolicy: policy, formState: fs}),
    enrollFeaturesInAlertPolicy: (policy, featureIds) =>
      enrollFeaturesMutation.mutateAsync({alertPolicy: policy, featureIds}),
    unenrollFeaturesInAlertPolicy: (policy, featureIds) =>
      unenrollFeaturesMutation.mutateAsync({alertPolicy: policy, featureIds}),
    archiveAlertPolicy: (alertPolicyId) => archiveAlertPolicyMutation.mutateAsync(alertPolicyId),
  };

  // General query status
  const meta = {
    isFetching,
    error,
  };

  return (
    <AlertPolicyContext.Provider value={{alertPolicies, actions, meta}}>
      {children}
    </AlertPolicyContext.Provider>
  );
};

export const FakeAlertPolicyProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    alertPolicies?: I.ImmutableListOf<AlertPolicy>;
    action?: (name: string) => () => any;
  }>
> = ({children, alertPolicies, action = () => () => null}) => {
  const value = React.useMemo(
    (): AlertPolicyContextValue => ({
      alertPolicies: alertPolicies?.toJS() ?? [],
      actions: {
        createAlertPolicy: action('createAlertPolicy'),
        updateAlertPolicy: action('updateAlertPolicy'),
        enrollFeaturesInAlertPolicy: action('enrollFeaturesInAlertPolicy'),
        unenrollFeaturesInAlertPolicy: action('unenrollFeaturesInAlertPolicy'),
        archiveAlertPolicy: action('archiveAlertPolicy'),
      },
      meta: {
        isFetching: alertPolicies?.size === 0, // should be ok for tests
        error: null,
      },
    }),
    [alertPolicies, action]
  );

  return <AlertPolicyContext.Provider value={value}>{children}</AlertPolicyContext.Provider>;
};

export function useAlertPolicies(): AlertPolicyContextValue {
  const value = React.useContext(AlertPolicyContext);

  if (!value) {
    throw new Error('useAlertPolicies must be beneath an AlertPolicyContextProvider');
  }

  return value;
}

export interface ParcelEligibility {
  parcelCount: number;
  eligible: boolean;
}

export function useParcelEligibility(
  featureCollectionId: number,
  featureId: number,
  {enabled = true}: {enabled?: boolean} = {}
) {
  return useQuery({
    queryKey: ['parcelEligibility', featureCollectionId, featureId],
    queryFn: async (): Promise<ParcelEligibility> => {
      const body = await api.featureCollections
        .features(featureCollectionId)
        .parcelAlertEligibility(featureId);
      return {
        parcelCount: body.parcel_count,
        eligible: body.eligible,
      };
    },
    enabled,
  });
}

/** Sorted so callers holding the same ids in a different order share a cache entry. */
function alertPoliciesQueryKey(featureCollectionIds: (number | string)[]) {
  return ['alertPolicies', 'multipleFcs', [...featureCollectionIds].sort()];
}

/**
 * Policies belong to primary feature collections, so pass only those. Overlay
 * feature collections never own policies — a policy references its overlays via
 * `properties.includeOverlays` and `properties.excludeOverlays` — so asking for
 * an overlay’s policies is a guaranteed-empty request.
 */
async function fetchAlertPoliciesForFeatureCollections(
  featureCollectionIds: (number | string)[]
): Promise<AlertPolicy[]> {
  if (featureCollectionIds.length === 0) {
    return [];
  }

  // fetch policies for given fcs in parallel w promise.all
  const promises = featureCollectionIds.map(async (featureCollectionId) => {
    const apiAlertPolicies: I.ImmutableListOf<ApiAlertPolicy> = (
      await api.featureCollections.getAlertPolicies(featureCollectionId)
    ).get('data');
    return (camelCaseKeys(apiAlertPolicies.toJS()) as AlertPolicy[]).map(normalizeAlertPolicy);
  });

  const results = await Promise.all(promises);
  // flatten the array of arrays into a single array of alert policies
  return results.flat();
}

/**
 * utility hook to fetch all AlertPolicies across multiple portfolios.
 * Useful when you want to look at all existing alert policies across an org.
 * If you want to actually manipulate alert policies, keep it at the FC level using useAlertPolicies().e
 *
 * One request per feature collection, so only prefetch when something rendered
 * depends on the policies. To answer a question at click time, use
 * useFetchAlertPoliciesForFeatureCollections() with just the ids in play.
 */
export function useAlertPoliciesForFeatureCollections(featureCollectionIds: (number | string)[]) {
  return useQuery({
    queryKey: alertPoliciesQueryKey(featureCollectionIds),
    queryFn: () => fetchAlertPoliciesForFeatureCollections(featureCollectionIds),
    enabled: featureCollectionIds.length > 0,
  });
}

/**
 * On-demand counterpart to useAlertPoliciesForFeatureCollections, for handlers
 * that need policies only once the user acts. Shares the hook’s cache, so a
 * repeated action doesn’t refetch.
 */
export function useFetchAlertPoliciesForFeatureCollections() {
  const queryClient = useQueryClient();

  return React.useCallback(
    (featureCollectionIds: (number | string)[]): Promise<AlertPolicy[]> =>
      queryClient.fetchQuery({
        queryKey: alertPoliciesQueryKey(featureCollectionIds),
        queryFn: () => fetchAlertPoliciesForFeatureCollections(featureCollectionIds),
      }),
    [queryClient]
  );
}
